import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Settings, Users, Save, Loader2, RefreshCw,
  ShoppingBag, Calendar, MessageSquare, Heart, Stethoscope,
  Trophy, Globe, Bell, BookOpen, Vote, Sparkles, Wrench,
  Palette, Mail, Link as LinkIcon, FileText, Plus, Trash2, Building2, User as UserIcon,
  Inbox, FileLock2,
} from 'lucide-react';
import axios from 'axios';
import SupportInbox from '../components/SupportInbox';
import PartnerAdmin from '../components/PartnerAdmin';
import LegalDocumentsPanel from '../components/LegalDocumentsPanel';
import ImageUploadInput from '../components/ImageUploadInput';
import { useSupportTicketPulse } from '../hooks/useSupportTicketPulse';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  commerce: ShoppingBag,
  community: Users,
  pets: Heart,
  content: BookOpen,
  governance: Vote,
  events: Calendar,
  platform: Globe
};

export default function ClubAdminDashboard() {
  const { user } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [clubDetails, setClubDetails] = useState({});
  const [featureDefinitions, setFeatureDefinitions] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingClub, setSavingClub] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [selectedClub, setSelectedClub] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [profileForm, setProfileForm] = useState({});
  const [profileDirty, setProfileDirty] = useState(false);
  const [patronList, setPatronList] = useState([]);
  const [patronForm, setPatronForm] = useState(null); // null = not editing, {} = new, {id:...} = editing
  const [savingPatron, setSavingPatron] = useState(false);

  const isAdmin = user?.is_admin || user?.is_operator_admin || user?.is_platform_admin || user?.is_sysadmin;

  // Polls active support ticket count every 30s + fires browser notifications on growth
  const { count: supportTicketCount } = useSupportTicketPulse({ enabled: !!isAdmin });

  useEffect(() => {
    if (user && isAdmin) fetchData();
  }, [user]);

  // When selected club changes, load its profile into the form
  useEffect(() => {
    if (selectedClub && clubDetails[selectedClub]) {
      const club = clubDetails[selectedClub];
      setProfileForm({
        name: club.name || '',
        tagline: club.tagline || '',
        description: club.description || '',
        mission: club.mission || '',
        primary_color: club.primary_color || '#1e3a5f',
        secondary_color: club.secondary_color || '#dc2626',
        accent_color: club.accent_color || '#ffffff',
        website_url: club.website_url || '',
        email: club.email || '',
        logo_url: club.logo_url || '',
        banner_url: club.banner_url || ''
});
      setProfileDirty(false);
    }
  }, [selectedClub, clubDetails]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [defsRes, clubsRes, allClubsRes] = await Promise.all([
        axios.get(`${API}/api/feature-toggles/definitions`),
        axios.get(`${API}/api/feature-toggles/all-clubs`),
        axios.get(`${API}/api/clubs/`)
      ]);
      setFeatureDefinitions(defsRes.data.features);
      // Filter to AADC only for now — other clubs (worldpets / club-canino)
      // are scaffolded but not active. Hide them from the admin tab strip
      // so admins don't get presented with management surfaces for clubs
      // they don't actually run. Re-enable when real second-tenant lands.
      const visibleClubs = (clubsRes.data.clubs || []).filter(
        (c) => c.slug === 'all-american-dog-club',
      );
      setClubs(visibleClubs);

      // Build lookup of full club details by slug
      const details = {};
      for (const club of allClubsRes.data) {
        details[club.slug] = club;
      }
      setClubDetails(details);

      if (!selectedClub && visibleClubs.length > 0) {
        setSelectedClub(visibleClubs[0].slug);
      }
      
      // Fetch patrons
      try {
        const patronsRes = await axios.get(`${API}/api/patrons/all`);
        setPatronList(patronsRes.data);
      } catch (e) {
        // May fail if no patrons yet
      }
    } catch (error) {
      toast.error('Failed to load club data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (clubSlug, featureKey, value) => {
    setPendingChanges(prev => ({
      ...prev,
      [clubSlug]: { ...(prev[clubSlug] || {}), [featureKey]: value }
    }));
    setClubs(prev => prev.map(club => {
      if (club.slug === clubSlug) {
        return { ...club, features: { ...club.features, [featureKey]: value } };
      }
      return club;
    }));
  };

  const saveFeatureChanges = async (clubSlug) => {
    const changes = pendingChanges[clubSlug];
    if (!changes || Object.keys(changes).length === 0) return;

    setSavingClub(clubSlug);
    try {
      await axios.put(
        `${API}/api/feature-toggles/club/${clubSlug}`,
        { features: changes });
      toast.success('Feature changes saved');
      setPendingChanges(prev => { const n = { ...prev }; delete n[clubSlug]; return n; });
    } catch (error) {
      toast.error('Failed to save feature changes');
    } finally {
      setSavingClub(null);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
    setProfileDirty(true);
  };

  const saveProfile = async () => {
    const club = clubDetails[selectedClub];
    if (!club) return;

    setSavingProfile(true);
    try {
      const updateData = {};
      // Only send changed fields
      for (const [key, value] of Object.entries(profileForm)) {
        if (value !== (club[key] || '')) {
          updateData[key] = value || null;
        }
      }
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        setSavingProfile(false);
        return;
      }

      const res = await axios.put(
        `${API}/api/clubs/${club.id}`,
        updateData);
      // Update local state
      setClubDetails(prev => ({ ...prev, [selectedClub]: res.data }));
      setProfileDirty(false);
      toast.success('Club profile updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const newPatronDefaults = () => ({
    name: '', type: 'individual', club_id: null, contact_name: '', contact_email: '',
    website_url: '', logo_url: '', tagline: '', public_message: '',
    contribution_type: 'financial', contribution_description: '', contribution_amount: '',
    is_recurring: false, visibility: 'public', tier: 'supporter', status: 'active'
  });

  const savePatron = async () => {
    if (!patronForm?.name) { toast.error('Name is required'); return; }
    setSavingPatron(true);
    try {
      const payload = { ...patronForm };
      if (payload.contribution_amount === '' || payload.contribution_amount === null) {
        payload.contribution_amount = null;
      } else {
        payload.contribution_amount = parseFloat(payload.contribution_amount) || null;
      }
      // Set club_id from selected club if not explicitly set
      if (selectedClub && !payload.club_id && clubDetails[selectedClub]) {
        payload.club_id = clubDetails[selectedClub].id;
      }

      if (patronForm.id) {
        const res = await axios.put(`${API}/api/patrons/${patronForm.id}`, payload);
        setPatronList(prev => prev.map(p => p.id === patronForm.id ? res.data : p));
        toast.success('Patron updated');
      } else {
        const res = await axios.post(`${API}/api/patrons`, payload);
        setPatronList(prev => [...prev, res.data]);
        toast.success('Patron added');
      }
      setPatronForm(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save patron');
    } finally {
      setSavingPatron(false);
    }
  };

  const deletePatron = async (id) => {
    if (!confirm('Delete this patron?')) return;
    try {
      await axios.delete(`${API}/api/patrons/${id}`);
      setPatronList(prev => prev.filter(p => p.id !== id));
      toast.success('Patron deleted');
    } catch (error) {
      toast.error('Failed to delete patron');
    }
  };

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card><CardContent className="py-8 px-12 text-center text-muted-foreground">
          Access restricted to club administrators.
        </CardContent></Card>
      </div>
    );
  }

  const activeClubFeatures = clubs.find(c => c.slug === selectedClub);
  const hasPendingFeatures = selectedClub && pendingChanges[selectedClub] && Object.keys(pendingChanges[selectedClub]).length > 0;

  const getCategories = () => {
    const categories = {};
    Object.entries(featureDefinitions).forEach(([key, def]) => {
      const cat = def.category || 'other';
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push({
        key,
        ...def,
        enabled: activeClubFeatures?.features[key] ?? def.default
      });
    });
    return categories;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="club-admin-title">
              Club Administration
            </h1>
            <p className="text-sm text-gray-600">Manage profile, features and settings for your clubs</p>
          </div>
          <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="refresh-clubs-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : clubs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No clubs found.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Club Selector */}
            {clubs.length > 1 && (
              <Tabs value={selectedClub} onValueChange={setSelectedClub}>
                <TabsList className="bg-white border" data-testid="club-selector-tabs">
                  {clubs.map(club => (
                    <TabsTrigger key={club.slug} value={club.slug} data-testid={`club-tab-${club.slug}`}>
                      {club.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}

            {activeClubFeatures && (
              <>
                {/* Profile / Features tab switcher */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList data-testid="admin-section-tabs" className="flex flex-wrap h-auto gap-1 justify-start">
                    <TabsTrigger value="profile" data-testid="tab-profile">
                      <FileText className="h-4 w-4 mr-1.5" /> Profile
                    </TabsTrigger>
                    <TabsTrigger value="features" data-testid="tab-features">
                      <Settings className="h-4 w-4 mr-1.5" /> Features
                    </TabsTrigger>
                    <TabsTrigger value="patrons" data-testid="tab-patrons">
                      <Heart className="h-4 w-4 mr-1.5" /> Patrons
                    </TabsTrigger>
                    <TabsTrigger value="support" data-testid="tab-support">
                      <Inbox className="h-4 w-4 mr-1.5" /> Support
                      {supportTicketCount > 0 && (
                        <span
                          className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none"
                          data-testid="club-support-badge"
                        >
                          {supportTicketCount > 99 ? '99+' : supportTicketCount}
                        </span>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="partners" data-testid="tab-partners">
                      <Building2 className="h-4 w-4 mr-1.5" /> Partners
                    </TabsTrigger>
                    <TabsTrigger value="legal" data-testid="tab-legal">
                      <FileLock2 className="h-4 w-4 mr-1.5" /> Legal
                    </TabsTrigger>
                    <TabsTrigger value="pulse" data-testid="tab-pulse" asChild>
                      <Link to={`/club-admin/${selectedClub}/pulse-settings`} className="inline-flex items-center">
                        <Sparkles className="h-4 w-4 mr-1.5" /> Pulse
                      </Link>
                    </TabsTrigger>
                  </TabsList>

                  {/* ===== PROFILE TAB ===== */}
                  <TabsContent value="profile" className="space-y-4 mt-4">
                    <Card data-testid="club-profile-editor">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle>Club Profile</CardTitle>
                            <CardDescription>Edit your club's public-facing information</CardDescription>
                          </div>
                          <Button
                            onClick={saveProfile}
                            disabled={!profileDirty || savingProfile}
                            data-testid="save-profile-btn"
                          >
                            {savingProfile ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Save className="h-4 w-4 mr-2" />
                            )}
                            Save Profile
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Basic Info */}
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Basic Information</h3>
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="club-name">Club Name</Label>
                              <Input
                                id="club-name"
                                value={profileForm.name || ''}
                                onChange={e => handleProfileChange('name', e.target.value)}
                                data-testid="input-club-name"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="club-tagline">Tagline</Label>
                              <Input
                                id="club-tagline"
                                value={profileForm.tagline || ''}
                                onChange={e => handleProfileChange('tagline', e.target.value)}
                                placeholder="A short catchy phrase"
                                data-testid="input-club-tagline"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="club-description">Description</Label>
                            <textarea
                              id="club-description"
                              rows={3}
                              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              value={profileForm.description || ''}
                              onChange={e => handleProfileChange('description', e.target.value)}
                              data-testid="input-club-description"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="club-mission">Mission Statement</Label>
                            <textarea
                              id="club-mission"
                              rows={2}
                              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              value={profileForm.mission || ''}
                              onChange={e => handleProfileChange('mission', e.target.value)}
                              data-testid="input-club-mission"
                            />
                          </div>
                        </div>

                        {/* Branding Images */}
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                            <Building2 className="h-4 w-4" /> Logo & Banner
                          </h3>
                          <div className="grid gap-4 sm:grid-cols-2">
                            <ImageUploadInput
                              label="Logo"
                              value={profileForm.logo_url || ''}
                              onChange={(url) => handleProfileChange('logo_url', url)}
                              kind="logo"
                              placeholder="https://example.com/logo.png"
                              testId="club-logo"
                              previewClassName="h-20 max-w-full object-contain rounded border bg-muted/30 p-1"
                            />
                            <ImageUploadInput
                              label="Banner Image"
                              value={profileForm.banner_url || ''}
                              onChange={(url) => handleProfileChange('banner_url', url)}
                              kind="banner"
                              placeholder="https://example.com/banner.jpg"
                              testId="club-banner"
                              previewClassName="h-24 w-full object-cover rounded border"
                            />
                          </div>
                        </div>

                        {/* Branding Colors */}
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                            <Palette className="h-4 w-4" /> Branding Colors
                          </h3>
                          <div className="grid gap-4 sm:grid-cols-3">
                            <div className="space-y-2">
                              <Label htmlFor="primary-color">Primary Color</Label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  id="primary-color"
                                  value={profileForm.primary_color || '#1e3a5f'}
                                  onChange={e => handleProfileChange('primary_color', e.target.value)}
                                  className="h-10 w-14 rounded border cursor-pointer"
                                  data-testid="input-primary-color"
                                />
                                <Input
                                  value={profileForm.primary_color || ''}
                                  onChange={e => handleProfileChange('primary_color', e.target.value)}
                                  className="font-mono text-sm"
                                  placeholder="#1e3a5f"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="secondary-color">Secondary Color</Label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  id="secondary-color"
                                  value={profileForm.secondary_color || '#dc2626'}
                                  onChange={e => handleProfileChange('secondary_color', e.target.value)}
                                  className="h-10 w-14 rounded border cursor-pointer"
                                  data-testid="input-secondary-color"
                                />
                                <Input
                                  value={profileForm.secondary_color || ''}
                                  onChange={e => handleProfileChange('secondary_color', e.target.value)}
                                  className="font-mono text-sm"
                                  placeholder="#dc2626"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="accent-color">Accent Color</Label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  id="accent-color"
                                  value={profileForm.accent_color || '#ffffff'}
                                  onChange={e => handleProfileChange('accent_color', e.target.value)}
                                  className="h-10 w-14 rounded border cursor-pointer"
                                  data-testid="input-accent-color"
                                />
                                <Input
                                  value={profileForm.accent_color || ''}
                                  onChange={e => handleProfileChange('accent_color', e.target.value)}
                                  className="font-mono text-sm"
                                  placeholder="#ffffff"
                                />
                              </div>
                            </div>
                          </div>
                          {/* Color Preview */}
                          <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                            <span className="text-xs text-muted-foreground">Preview:</span>
                            <div
                              className="h-8 w-24 rounded"
                              style={{ background: profileForm.primary_color || '#1e3a5f' }}
                              data-testid="preview-primary-color"
                            />
                            <div
                              className="h-8 w-24 rounded"
                              style={{ background: profileForm.secondary_color || '#dc2626' }}
                              data-testid="preview-secondary-color"
                            />
                            <div
                              className="h-8 w-24 rounded border"
                              style={{ background: profileForm.accent_color || '#ffffff' }}
                              data-testid="preview-accent-color"
                            />
                          </div>
                        </div>

                        {/* Contact */}
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                            <Mail className="h-4 w-4" /> Contact & Web
                          </h3>
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="club-website">Website URL</Label>
                              <Input
                                id="club-website"
                                value={profileForm.website_url || ''}
                                onChange={e => handleProfileChange('website_url', e.target.value)}
                                placeholder="https://yourclub.com"
                                data-testid="input-club-website"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="club-email">Contact Email</Label>
                              <Input
                                id="club-email"
                                type="email"
                                value={profileForm.email || ''}
                                onChange={e => handleProfileChange('email', e.target.value)}
                                placeholder="info@yourclub.com"
                                data-testid="input-club-email"
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* ===== FEATURES TAB ===== */}
                  <TabsContent value="features" className="space-y-4 mt-4">
                    {/* Summary */}
                    <Card data-testid={`club-summary-${activeClubFeatures.slug}`}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle>{activeClubFeatures.name} - Features</CardTitle>
                            <CardDescription>
                              {Object.values(activeClubFeatures.features).filter(Boolean).length} of {Object.keys(activeClubFeatures.features).length} features enabled
                            </CardDescription>
                          </div>
                          {hasPendingFeatures && (
                            <Button onClick={() => saveFeatureChanges(selectedClub)} disabled={savingClub === selectedClub}
                              data-testid={`save-club-btn-${activeClubFeatures.slug}`}>
                              {savingClub === selectedClub ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              ) : (
                                <Save className="h-4 w-4 mr-2" />
                              )}
                              Save Changes
                            </Button>
                          )}
                        </div>
                      </CardHeader>
                    </Card>

                    {/* Feature Categories */}
                    <div className="space-y-4">
                      {Object.entries(getCategories()).map(([catName, features]) => {
                        const CatIcon = CATEGORY_ICONS[catName] || Settings;
                        const enabledCount = features.filter(f => f.enabled).length;

                        return (
                          <Card key={catName} data-testid={`category-${catName}`}>
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <CatIcon className="h-5 w-5 text-muted-foreground" />
                                  <CardTitle className="text-base capitalize">{catName}</CardTitle>
                                  <Badge variant="secondary" className="text-xs">
                                    {enabledCount}/{features.length}
                                  </Badge>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="grid gap-3 sm:grid-cols-2">
                                {features.map((feature) => (
                                  <div
                                    key={feature.key}
                                    className={`flex items-center justify-between gap-3 p-3 rounded-lg border overflow-hidden transition-colors ${
                                      feature.enabled ? 'bg-green-50/50 border-green-200' : 'bg-gray-50 border-gray-200'
                                    }`}
                                    data-testid={`feature-${activeClubFeatures.slug}-${feature.key}`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium break-words">{feature.label}</p>
                                      <p className="text-xs text-muted-foreground truncate">{feature.description}</p>
                                    </div>
                                    <Switch
                                      checked={feature.enabled}
                                      onCheckedChange={(val) => handleToggle(activeClubFeatures.slug, feature.key, val)}
                                      data-testid={`switch-${activeClubFeatures.slug}-${feature.key}`}
                                      className="shrink-0"
                                    />
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </TabsContent>

                  {/* ===== PATRONS TAB ===== */}
                  <TabsContent value="patrons" className="space-y-4 mt-4">
                    <Card data-testid="patrons-admin">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle>Manage Patrons</CardTitle>
                            <CardDescription>Add and manage sponsors, partners, and supporters</CardDescription>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => setPatronForm(newPatronDefaults())}
                            data-testid="add-patron-btn"
                          >
                            <Plus className="h-4 w-4 mr-1" /> Add Patron
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {/* Patron Form (inline) */}
                        {patronForm && (
                          <div className="border rounded-lg p-4 mb-4 bg-gray-50 space-y-3" data-testid="patron-form">
                            <h4 className="text-sm font-semibold">{patronForm.id ? 'Edit Patron' : 'New Patron'}</h4>
                            <div className="grid gap-3 sm:grid-cols-2">
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Name *</label>
                                <input className="w-full rounded-md border px-3 py-1.5 text-sm" value={patronForm.name} onChange={e => setPatronForm(p => ({...p, name: e.target.value}))} data-testid="patron-name-input" />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Type</label>
                                <select className="w-full rounded-md border px-3 py-1.5 text-sm" value={patronForm.type} onChange={e => setPatronForm(p => ({...p, type: e.target.value}))}>
                                  <option value="individual">Individual</option>
                                  <option value="organization">Organization</option>
                                </select>
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Tier</label>
                                <select className="w-full rounded-md border px-3 py-1.5 text-sm" value={patronForm.tier} onChange={e => setPatronForm(p => ({...p, tier: e.target.value}))}>
                                  <option value="supporter">Supporter</option>
                                  <option value="sponsor">Sponsor</option>
                                  <option value="champion">Champion</option>
                                  <option value="founding">Founding Patron</option>
                                </select>
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Visibility</label>
                                <select className="w-full rounded-md border px-3 py-1.5 text-sm" value={patronForm.visibility} onChange={e => setPatronForm(p => ({...p, visibility: e.target.value}))}>
                                  <option value="public">Public</option>
                                  <option value="anonymous">Anonymous</option>
                                </select>
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Tagline</label>
                                <input className="w-full rounded-md border px-3 py-1.5 text-sm" value={patronForm.tagline} onChange={e => setPatronForm(p => ({...p, tagline: e.target.value}))} placeholder="Short description" />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Website</label>
                                <input className="w-full rounded-md border px-3 py-1.5 text-sm" value={patronForm.website_url} onChange={e => setPatronForm(p => ({...p, website_url: e.target.value}))} placeholder="https://..." />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Contribution Type</label>
                                <select className="w-full rounded-md border px-3 py-1.5 text-sm" value={patronForm.contribution_type} onChange={e => setPatronForm(p => ({...p, contribution_type: e.target.value}))}>
                                  <option value="financial">Financial</option>
                                  <option value="services">Services</option>
                                  <option value="supplies">Supplies</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Amount ($)</label>
                                <input type="number" className="w-full rounded-md border px-3 py-1.5 text-sm" value={patronForm.contribution_amount} onChange={e => setPatronForm(p => ({...p, contribution_amount: e.target.value}))} placeholder="Optional" />
                              </div>
                              <div className="sm:col-span-2 space-y-1">
                                <label className="text-xs font-medium text-gray-700">Contribution Description</label>
                                <input className="w-full rounded-md border px-3 py-1.5 text-sm" value={patronForm.contribution_description} onChange={e => setPatronForm(p => ({...p, contribution_description: e.target.value}))} placeholder="What they contribute" />
                              </div>
                              <div className="sm:col-span-2 space-y-1">
                                <label className="text-xs font-medium text-gray-700">Public Message</label>
                                <textarea className="w-full rounded-md border px-3 py-1.5 text-sm" rows={2} value={patronForm.public_message} onChange={e => setPatronForm(p => ({...p, public_message: e.target.value}))} placeholder="Optional message displayed publicly" />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Contact Name</label>
                                <input className="w-full rounded-md border px-3 py-1.5 text-sm" value={patronForm.contact_name} onChange={e => setPatronForm(p => ({...p, contact_name: e.target.value}))} />
                              </div>
                              <div className="space-y-1">
                                <label className="text-xs font-medium text-gray-700">Contact Email</label>
                                <input className="w-full rounded-md border px-3 py-1.5 text-sm" value={patronForm.contact_email} onChange={e => setPatronForm(p => ({...p, contact_email: e.target.value}))} />
                              </div>
                            </div>
                            <div className="flex items-center gap-2 pt-2">
                              <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={patronForm.is_recurring} onChange={e => setPatronForm(p => ({...p, is_recurring: e.target.checked}))} />
                                Recurring contribution
                              </label>
                            </div>
                            <div className="flex gap-2 pt-2">
                              <Button size="sm" onClick={savePatron} disabled={savingPatron} data-testid="save-patron-btn">
                                {savingPatron ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                                {patronForm.id ? 'Update' : 'Add'} Patron
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setPatronForm(null)}>Cancel</Button>
                            </div>
                          </div>
                        )}

                        {/* Patron List */}
                        {patronList.length === 0 && !patronForm ? (
                          <p className="text-sm text-muted-foreground text-center py-8">No patrons yet. Add your first patron above.</p>
                        ) : (
                          <div className="space-y-2">
                            {patronList.map(patron => (
                              <div key={patron.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`patron-admin-${patron.id}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center shrink-0">
                                    {patron.type === 'organization'
                                      ? <Building2 className="w-4 h-4 text-gray-400" />
                                      : <UserIcon className="w-4 h-4 text-gray-400" />
                                    }
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">{patron.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {patron.tier} - {patron.contribution_type}
                                      {patron.contribution_amount ? ` - $${patron.contribution_amount}` : ''}
                                      {patron.status !== 'active' ? ` (${patron.status})` : ''}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex gap-1 shrink-0">
                                  <Button variant="outline" size="sm" onClick={() => setPatronForm({...patron, contribution_amount: patron.contribution_amount || ''})}>Edit</Button>
                                  <Button variant="ghost" size="sm" onClick={() => deletePatron(patron.id)}>
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* ===== SUPPORT TAB ===== */}
                  <TabsContent value="support" className="mt-4">
                    <SupportInbox />
                  </TabsContent>

                  {/* ===== PARTNERS TAB ===== */}
                  <TabsContent value="partners" className="mt-4">
                    <PartnerAdmin />
                  </TabsContent>

                  {/* ===== LEGAL TAB ===== */}
                  <TabsContent value="legal" className="mt-4">
                    <LegalDocumentsPanel
                      scope="club"
                      clubId={activeClubFeatures.id}
                      clubName={activeClubFeatures.name}
                    />
                  </TabsContent>
                </Tabs>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
